import { useId, type ReactElement, type ReactNode } from "react";
// eslint-disable-next-line no-restricted-imports
import { Combobox as ComboboxBase } from "@base-ui/react/combobox";
// eslint-disable-next-line no-restricted-imports
import { Select as SelectBase } from "@base-ui/react/select";
import {
    attachedListContentProps,
    PickerAttachedTrigger,
} from "./PickerAttachedTrigger";
import { PickerCellTrigger } from "./PickerCellTrigger";
import { PickerInlineSurface } from "./PickerPopup";
import {
    EMPTY_PICKER_CLASS_NAMES,
    PickerRootListContent,
    PickerRootShell,
} from "./PickerRootShell";
import {
    defaultChevronForPreset,
    isInputTriggerPreset,
    PickerInputTriggerPreset,
} from "./PickerRootTriggers";
import { PickerButtonTrigger } from "./PickerButtonTrigger";
import { PickerSelectCustomValue } from "./PickerSelectCustomValue";
import { usePickerListRoot } from "./usePickerListRoot";
import { useShallowStable } from "./usePickerStable";
import type {
    PickerItemValue,
    PickerSelectListProps,
    PickerSelectProps,
} from "./Picker.types";
const PickerSelectList = <T extends PickerItemValue>(
    props: PickerSelectListProps<T>,
): ReactElement => {
    const {
        multiple = false,
        value,
        defaultValue,
        onValueChange,
        parentSelection,
        indicateSelectedDescendants,
        items,
        sections,
        nested,
        defaultExpandAll,
        defaultExpandedValues,
        container,
        flyoutDefaults,
        flyoutQueryScope,
        flyoutAutoOpen,
        chevron,
        clearable,
        inline = false,
        cellEditor,
        autoHighlight = cellEditor !== undefined,
        filter,
        inputValue,
        defaultInputValue,
        onInputValueChange,
        emptyLabel,
        emptyBehavior,
        placeholder,
        selectableParents,
        renderValue,
        selectionSummary,
        renderFooter,
        status = "idle",
        loadingLabel,
        errorLabel,
        hasMore,
        onLoadMore,
        loadMoreStatus,
        open,
        defaultOpen = false,
        onOpenChange,
        disabled = false,
        readOnly = false,
        required = false,
        name,
        id,
        modal = false,
        onBlur,
        "aria-invalid": isInvalid = false,
        "data-forced-state": forcedState,
        className,
        classNames = EMPTY_PICKER_CLASS_NAMES,
        children,
    } = props;
    const attachedTitleId = useId();
    const isInputTrigger = isInputTriggerPreset(props.trigger);
    // Keeps the memoised cell trigger from re-rendering for an inline literal.
    const buttonProps = useShallowStable(props.buttonProps);
    const isSearchable = props.searchable === true;
    // Base UI Select for a plain button; Combobox once text filters the list
    // (popup search bar or a text trigger) or the list renders inline.
    const kind =
        isSearchable || inline || isInputTrigger ? "combobox" : "select";
    const hasPopupSearch = isSearchable && !isInputTrigger;
    const isFilterable = props.filterable !== false;
    const root = usePickerListRoot({
        kind,
        isMultiple: multiple,
        value,
        defaultValue,
        onValueChange,
        parentSelection,
        indicateSelectedDescendants,
        items,
        sections,
        nested,
        defaultExpandAll,
        defaultExpandedValues,
        container,
        flyoutDefaults,
        flyoutQueryScope,
        flyoutAutoOpen,
        renderValue,
        selectionSummary,
        selectableParents,
        filter,
        shouldFilter: kind === "combobox" && isFilterable,
        shouldBypassSelectedLabelQuery: isInputTrigger && !multiple,
        hasTypedCommit: isInputTrigger,
        hasPopupSearch,
        open,
        defaultOpen,
        isAlwaysOpen: inline,
        isInline: inline,
        isAutoHighlight: autoHighlight,
        cellEditor,
        onOpenChange,
        inputValue,
        defaultInputValue,
        onInputValueChange,
        emptyBehavior,
        status,
        hasMore,
        onLoadMore,
        loadMoreStatus,
        renderFooter,
        disabled,
        isInvalid,
        classNames,
        forcedState,
        chevron: chevron ?? defaultChevronForPreset(props.trigger),
        explicitChevron: chevron,
        onBlur,
    });

    const renderTrigger = (): ReactNode => {
        if (props.trigger === "input" || props.trigger === "cellInput") {
            return (
                <PickerInputTriggerPreset
                    trigger={props.trigger}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    size={props.size}
                    clearable={clearable}
                    className={className}
                    ref={props.ref}
                />
            );
        }
        if (props.trigger === "cell") {
            return (
                <PickerCellTrigger
                    placeholder={placeholder}
                    readOnly={readOnly}
                    clearable={clearable}
                    buttonProps={buttonProps}
                    className={className}
                    ref={props.ref}
                />
            );
        }
        if (props.trigger === "attached") {
            return (
                <PickerAttachedTrigger
                    attachedTo={props.attachedTo}
                    title={props.title}
                    icon={props.icon}
                    placeholder={placeholder}
                    className={className}
                    ref={props.ref}
                />
            );
        }
        return (
            <PickerButtonTrigger
                render={props.trigger}
                placeholder={placeholder}
                clearable={clearable}
                className={className}
                ref={props.ref}
            />
        );
    };
    const triggerNode = inline ? undefined : renderTrigger();
    const attachedContentProps =
        props.trigger === "attached"
            ? attachedListContentProps({
                  title: props.title,
                  titleId: attachedTitleId,
              })
            : {};

    let content = children;
    if (items !== undefined || sections !== undefined) {
        content = (
            <PickerRootListContent
                {...attachedContentProps}
                root={root}
                trigger={triggerNode}
                hasPopupSearch={hasPopupSearch}
                isInline={inline}
                searchPlaceholder={props.searchPlaceholder}
                searchAutoFocus={props.searchAutoFocus}
                searchRef={props.searchRef}
                status={status}
                loadingLabel={loadingLabel}
                errorLabel={errorLabel}
                emptyLabel={emptyLabel}
                emptyBehavior={emptyBehavior}
            >
                {children}
            </PickerRootListContent>
        );
    } else if (inline) {
        content = <PickerInlineSurface>{children}</PickerInlineSurface>;
    }

    const sharedRootProps = {
        multiple,
        value: root.baseValue,
        onValueChange: root.handleBaseValueChange,
        open: root.isOpen,
        onOpenChange: root.handleBaseOpenChange,
        disabled,
        readOnly,
        required,
        name,
        id,
        modal,
    };

    return (
        <PickerRootShell root={root}>
            {kind === "combobox" ? (
                <ComboboxBase.Root<T, boolean>
                    {...sharedRootProps}
                    inline={inline}
                    autoHighlight={autoHighlight}
                    inputValue={root.query}
                    onInputValueChange={(nextQuery, details) =>
                        root.handleInputValueChange(nextQuery, details.reason)
                    }
                    itemToStringLabel={
                        isInputTrigger ? root.selection.labelFor : undefined
                    }
                >
                    {content}
                </ComboboxBase.Root>
            ) : (
                <SelectBase.Root<T, boolean> {...sharedRootProps}>
                    {content}
                </SelectBase.Root>
            )}
        </PickerRootShell>
    );
};

// Implemented over `Props<T, boolean>` (the widest `Multiple`) and narrowed at
// the export: the union is discriminated by `multiple` for callers, while the
// implementation only needs the value shapes both branches share.
const PickerSelectImpl = <T extends PickerItemValue>(
    props: PickerSelectProps<T, boolean>,
): ReactElement => {
    if (props.allowCustomValue === true) {
        return <PickerSelectCustomValue {...props} />;
    }
    return <PickerSelectList {...props} />;
};

/**
 * The one list root. `trigger` is a `Button` element (omitted: primary md),
 * the chromeless cell button, the Input field, the chromeless cell input or
 * the `attached` strip (popup grows upward out of `attachedTo`);
 * button triggers may add a popup search (`searchable`), text triggers filter as they type
 * and may keep the typed text as a `string` value (`allowCustomValue`); either
 * search filters the rows unless `filterable={false}`. Renders its own trigger
 * and list from `items` or `sections` (nested via `children` for a tree or
 * flyout levels); without them, compose the parts manually as `children`.
 * `inline` drops the trigger and popup and renders the list in place,
 * chromeless; without a `Picker.Search` the list itself is the keyboard tab
 * stop.
 */
export const PickerSelect: <
    T extends PickerItemValue,
    Multiple extends boolean = false,
>(
    props: PickerSelectProps<T, Multiple>,
) => ReactElement = PickerSelectImpl;
